<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { adminApi } from '@/api/admin';
import type { SecretSummaryDto } from '@/api/types';
import SecretHistoryPanel from '@/components/SecretHistoryPanel.vue';
import { useSecretsStore } from '@/stores/secrets';
import { useEnvironmentsStore } from '@/stores/environments';

// Multi-slot secret editor. Operator enters values for one or more slots (base + every
// registered env) in a single dialog and saves them all in one batch. Per-slot reveal/delete
// is still available; an empty draft means "skip this slot" (use the row's Delete button to
// clear an existing value — there is no implicit clear-on-empty so a stray edit can't wipe a
// secret).
//
// Save model mirrors the config-editor's Save-all pattern (sequential writes, aggregated
// success/failure toast). The server has no transactional multi-secret write, so partial
// failures are reported per-row and the dialog stays open with the failed rows highlighted.

interface EditorTarget {
  scope: string;
  name: string;
}

interface SlotRow {
  slot: string;
  filled: boolean;
  // Server-side tombstone state at last load/save. The "operator-current" intent is
  // `tombstoneDraft`; comparing the two is how we know whether this row needs a write.
  tombstone: boolean;
  draft: string;
  tombstoneDraft: boolean;
  revealed: string | null;
  showValue: boolean;
  error: string | null;
  busy: boolean;
}

const props = defineProps<{ target: EditorTarget | null }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const store = useSecretsStore();
const envStore = useEnvironmentsStore();
const confirm = useConfirm();
const toast = useToast();
const { slotChoices } = storeToRefs(envStore);

const rows = ref<SlotRow[]>([]);
const busy = ref(false);
const loadError = ref<string | null>(null);

const BASE_SLOT = '_base';

const scopeColonName = computed(() =>
  props.target ? `${props.target.scope}:${props.target.name}` : '');

const anyToSave = computed(() => rows.value.some(rowIsDirty));

// Mirrors the server-side invariant: every secret reference must have a _base entry. If no
// _base entry exists yet (new secret), the save batch must include a _base write — either
// a value or an explicit tombstone.
const baseRow = computed(() => rows.value.find((r) => r.slot === BASE_SLOT) ?? null);
const baseMissingForNewSecret = computed(() => {
  const b = baseRow.value;
  if (!b) return false;
  if (b.filled) return false;          // already established server-side
  return !rowIsDirty(b);                // not being established by this save either
});
const saveBlockedReason = computed<string | null>(() => {
  if (!anyToSave.value) return null;
  if (baseMissingForNewSecret.value) {
    return 'Every secret must have a _base entry. Set a value for _base, or use Override with null, before saving.';
  }
  return null;
});

function rowIsDirty(r: SlotRow): boolean {
  // Tombstone toggle changes are dirty regardless of the draft input.
  if (r.tombstoneDraft !== r.tombstone) return true;
  // Tombstoned rows can't carry a value write; the draft is ignored.
  if (r.tombstoneDraft) return false;
  if (r.draft === '') return false;
  // If the row was revealed and the draft matches the revealed value, this is a no-op.
  return r.revealed === null || r.draft !== r.revealed;
}

watch(
  () => props.target,
  async (target) => {
    rows.value = [];
    loadError.value = null;
    if (!target) return;
    await initRows(target);
  },
  { immediate: true },
);

async function initRows(target: EditorTarget) {
  busy.value = true;
  try {
    // Fetch by scope filter so we get every slot's entry in one trip. The store's `list` is
    // driven by SecretsView's filters and may exclude rows we need here.
    let filled: SecretSummaryDto[];
    try {
      filled = await adminApi.listSecrets(undefined, target.scope);
    } catch (e) {
      loadError.value = (e as Error).message;
      filled = [];
    }
    const byThisRef = new Map<string, SecretSummaryDto>();
    for (const s of filled) {
      if (s.name === target.name) byThisRef.set(s.slot, s);
    }
    rows.value = slotChoices.value.map((slot) => {
      const existing = byThisRef.get(slot);
      const tombstone = existing?.isTombstone ?? false;
      return {
        slot,
        filled: existing !== undefined,
        tombstone,
        tombstoneDraft: tombstone,
        draft: '',
        revealed: null,
        showValue: false,
        error: null,
        busy: false,
      };
    });
  } finally {
    busy.value = false;
  }
}

async function reveal(row: SlotRow) {
  if (!props.target) return;
  row.busy = true;
  row.error = null;
  try {
    const result = await store.reveal(row.slot, scopeColonName.value);
    // Reveal on a tombstone returns Value === "" plus IsTombstone === true. There's nothing
    // to surface in the input — the badge already tells the story — so we just keep the
    // tombstone state and skip populating the draft.
    if (result.isTombstone) {
      row.tombstone = true;
      row.tombstoneDraft = true;
      row.revealed = null;
      row.draft = '';
      row.showValue = false;
    } else {
      row.revealed = result.value;
      row.draft = result.value;
      row.showValue = true;
    }
  } catch (e) {
    row.error = (e as Error).message;
  } finally {
    row.busy = false;
  }
}

function hide(row: SlotRow) {
  row.showValue = false;
}

function toggleTombstone(row: SlotRow) {
  // Flipping in either direction. When entering the tombstone state, clear the draft so a
  // stray earlier keystroke doesn't get carried forward into the save.
  if (!row.tombstoneDraft) {
    row.tombstoneDraft = true;
    row.draft = '';
    row.showValue = false;
  } else {
    row.tombstoneDraft = false;
  }
}

// Per-row history drawer state. Only one drawer at a time; `historySlot` names the row
// whose history is currently open. Stage event from the panel populates that row's draft
// (or toggles tombstone mode); the operator then Saves to commit as a new version.
const historyVisible = ref(false);
const historySlot = ref<string | null>(null);

function openHistory(row: SlotRow) {
  historySlot.value = row.slot;
  historyVisible.value = true;
}

function onHistoryStage(payload: { slot: string; scopeColonName: string; value: string; isTombstone: boolean }) {
  const target = rows.value.find((r) => r.slot === payload.slot);
  if (!target) return;
  if (payload.isTombstone) {
    target.tombstoneDraft = true;
    target.draft = '';
    target.showValue = false;
  } else {
    target.tombstoneDraft = false;
    target.draft = payload.value;
    target.revealed = payload.value;
    target.showValue = true;
  }
  toast.add({
    severity: 'info',
    summary: 'Version staged',
    detail: `${payload.slot} — review and Save all to commit.`,
    life: 4000,
  });
}

function remove(row: SlotRow) {
  if (!props.target || !row.filled) return;
  const slot = row.slot;
  const ref = scopeColonName.value;
  confirm.require({
    message:
      `Delete ${slot} · ${ref}? `
      + `If '_base' has the same key, fallback will resurrect that value at composition time.`,
    header: 'Delete secret',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Delete',
    acceptProps: { severity: 'danger' },
    rejectLabel: 'Cancel',
    accept: async () => {
      row.busy = true;
      row.error = null;
      try {
        await store.remove(slot, ref);
        row.filled = false;
        row.draft = '';
        row.revealed = null;
        row.showValue = false;
      } catch (e) {
        row.error = (e as Error).message;
      } finally {
        row.busy = false;
      }
    },
  });
}

async function saveAll() {
  if (!props.target) return;
  const ref = scopeColonName.value;
  // _base goes first so the server's base-required invariant is satisfied before any
  // env-slot writes hit. `slotChoices` already returns ['_base', ...envs] in order, and
  // rows is built from that, so filtering in iteration order preserves "_base then envs".
  const dirty = rows.value.filter(rowIsDirty);
  if (dirty.length === 0) return;

  busy.value = true;
  let saved = 0;
  let failed = 0;
  for (const row of dirty) {
    row.error = null;
    row.busy = true;
    try {
      // Tombstone-dirty rows write an empty value (the server-side tombstone marker); plain
      // value rows write the draft. Either way the server returns success and the row's
      // local state catches up.
      const writeValue = row.tombstoneDraft ? '' : row.draft;
      await store.save(row.slot, ref, writeValue);
      row.filled = true;
      row.tombstone = row.tombstoneDraft;
      row.revealed = row.tombstoneDraft ? null : row.draft;
      saved++;
    } catch (e) {
      row.error = (e as Error).message;
      failed++;
    } finally {
      row.busy = false;
    }
  }
  busy.value = false;

  if (failed > 0) {
    toast.add({
      severity: 'error',
      summary: `${failed} slot${failed === 1 ? '' : 's'} failed to save`,
      life: 5000,
    });
  }
  if (saved > 0) {
    toast.add({
      severity: 'success',
      summary: saved === 1 ? 'Secret saved' : `${saved} slots saved`,
      life: 2500,
    });
  }
  if (failed === 0 && saved > 0) {
    emit('close');
  }
}
</script>

<template>
  <Dialog
    v-if="target"
    :visible="true"
    modal
    :closable="!busy"
    :close-on-escape="!busy"
    :style="{ width: '820px' }"
    @update:visible="(v: boolean) => { if (!v) emit('close'); }"
  >
    <template #header>
      <span class="ref mono">{{ scopeColonName }}</span>
    </template>

    <p class="hint">
      Enter a value for each slot you want to set; leave blank to skip. Use <strong>Delete</strong>
      to remove a slot's entry (composition will then fall back to <code class="mono">_base</code>),
      or <strong>Override with null</strong> to force this slot to resolve to JSON
      <code class="mono">null</code> even when <code class="mono">_base</code> has a value.
    </p>

    <Message v-if="loadError" severity="error" :closable="false" class="block-message">
      Could not load existing values: {{ loadError }}
    </Message>
    <Message v-if="saveBlockedReason" severity="warn" :closable="false" class="block-message">
      {{ saveBlockedReason }}
    </Message>

    <div class="slot-grid">
      <template v-for="row in rows" :key="row.slot">
        <!-- Col 1: slot name -->
        <span class="slot-label mono">{{ row.slot }}</span>

        <!-- Col 2: value (input, or tombstone pill) -->
        <div class="cell value-cell">
          <span v-if="row.tombstoneDraft" class="tombstone-pill mono">
            <i class="pi pi-ban" />&nbsp;→ null (overrides base)
          </span>
          <InputText
            v-else
            :type="row.showValue ? 'text' : 'password'"
            v-model="row.draft"
            :placeholder="row.filled ? (row.showValue ? '' : '••••••••') : 'No value (leave blank to skip)'"
            class="mono"
            fluid
            autocomplete="off"
            spellcheck="false"
            :name="`secret-${row.slot}`"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            :disabled="row.busy"
          />
        </div>

        <!-- Col 3: Reveal / Hide (only when there's a real value to peek at) -->
        <div class="cell">
          <Button
            v-if="row.filled && !row.tombstoneDraft && !row.showValue"
            label="Reveal"
            icon="pi pi-eye"
            severity="secondary"
            size="small"
            :disabled="row.busy"
            @click="reveal(row)"
          />
          <Button
            v-else-if="row.filled && !row.tombstoneDraft && row.showValue"
            label="Hide"
            icon="pi pi-eye-slash"
            severity="secondary"
            size="small"
            @click="hide(row)"
          />
        </div>

        <!-- Col 4: Override-with-null / Restore (always available per row) -->
        <div class="cell">
          <Button
            :label="row.tombstoneDraft ? 'Restore' : 'Override with null'"
            :icon="row.tombstoneDraft ? 'pi pi-undo' : 'pi pi-ban'"
            severity="secondary"
            size="small"
            outlined
            :disabled="row.busy"
            :title="row.tombstoneDraft
              ? 'Restore: revert this slot from tombstone back to its previous state'
              : 'Override with null: this slot will resolve to JSON null even if base has a value'"
            @click="toggleTombstone(row)"
          />
        </div>

        <!-- Col 5: History (only when the slot has a server-side entry — otherwise there
             are no versions to show). -->
        <div class="cell">
          <Button
            v-if="row.filled"
            icon="pi pi-history"
            severity="secondary"
            size="small"
            outlined
            :disabled="row.busy"
            title="Show previous versions of this slot's value; stage one as the new value"
            @click="openHistory(row)"
          />
        </div>

        <!-- Col 6: Delete (only when a server-side entry exists and not _base; the _base
             entry is part of the secret's identity and can only be removed via whole-secret
             delete in SecretsView). -->
        <div class="cell">
          <Button
            v-if="row.filled && row.slot !== BASE_SLOT"
            icon="pi pi-trash"
            severity="danger"
            size="small"
            outlined
            :disabled="row.busy"
            title="Delete this slot's entry (env will fall through to _base on compose)"
            @click="remove(row)"
          />
        </div>

        <Message
          v-if="row.error"
          severity="error"
          :closable="false"
          class="row-error"
        >{{ row.error }}</Message>
      </template>
    </div>

    <template #footer>
      <div class="spacer" />
      <Button label="Cancel" severity="secondary" :disabled="busy" @click="emit('close')" />
      <Button
        label="Save all"
        icon="pi pi-save"
        :disabled="busy || !anyToSave || saveBlockedReason !== null"
        @click="saveAll"
      />
    </template>
  </Dialog>

  <SecretHistoryPanel
    v-model:visible="historyVisible"
    :slot="historySlot"
    :scope-colon-name="scopeColonName"
    @stage="onHistoryStage"
  />
</template>

<style scoped>
.ref { font-size: 1rem; color: var(--p-text-color, #111827); }
.mono { font-family: 'Cascadia Code', Consolas, monospace; }
.hint { color: var(--retail-hint-text); font-size: 0.8125rem; margin: 0 0 0.75rem 0; }
.block-message { margin: 0 0 0.75rem 0; }

/* Six-column grid so the per-row buttons line up vertically: slot | value | reveal/hide |
   override/restore | history | delete. Fixed widths on the action columns are sized to the
   longest label they ever render (Override-with-null is the widest at 18 chars + icon), so
   when a row's button collapses to the alternate label (Hide → Reveal, Restore →
   Override-with-null) nothing shifts. The value column flexes to fill the remaining
   horizontal space. */
.slot-grid {
  display: grid;
  grid-template-columns: auto 1fr 7rem 12rem 3rem 3rem;
  row-gap: 0.4rem;
  column-gap: 0.5rem;
  align-items: center;
}
.slot-label {
  font-size: 0.875rem;
  color: var(--p-text-muted-color, #374151);
  padding-right: 0.25rem;
  min-width: 5rem;
}
.cell { display: flex; align-items: center; width: 100%; }
.cell :deep(.p-button) { width: 100%; justify-content: center; }
.value-cell { width: 100%; }
.row-error { grid-column: 1 / -1; margin: 0; }
.spacer { flex: 1; }

/* Tombstone-row indicator. Replaces the input visually so it's obvious at a glance that this
   row will write a null override on save. Same height as the input it replaces. */
.tombstone-pill {
  display: inline-flex;
  align-items: center;
  height: 2.25rem;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  color: var(--p-text-muted-color, #6b7280);
  background: var(--p-surface-100, #f3f4f6);
  border: 1px dashed var(--p-content-border-color, #d1d5db);
  border-radius: 4px;
  flex: 1;
}
.hint code {
  font-family: 'Cascadia Code', Consolas, monospace;
  background: var(--retail-code-bg);
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
}
</style>
